import React, { useEffect, useState, lazy, Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { useCurrentUser } from './hooks/useCurrentUser.js';
import api from './services/api.js';
import { InteractionStatus } from '@azure/msal-browser';
import { getMsalInstance, isMsalEnabled, loginWithRedirect } from './lib/auth.js';
import Layout from './components/Layout.jsx';
import CapGate from './components/CapGate.jsx';
import LoadingSpinner from './components/LoadingSpinner.jsx';

// AUDIT-BATCH-2 H1 (route-level code splitting): every routed page is a lazy chunk; the layout, auth shell and the
// pre-auth pages (login/reset/TOTP/ethics intake) stay in the entry bundle. A deploy replaces the hashed chunk files, so a tab
// opened before the deploy can fail to fetch a chunk (404 or an HTML fallback); reload once to pick up the new index.html.
function lazyPage(factory) {
  return lazy(() => factory().then((m) => {
    try { sessionStorage.removeItem('chunk-reload'); } catch { /* storage blocked */ }
    return m;
  }).catch((err) => {
    let reloaded = false;
    try { reloaded = sessionStorage.getItem('chunk-reload') === '1'; sessionStorage.setItem('chunk-reload', '1'); } catch { /* storage blocked */ }
    if (!reloaded) { window.location.reload(); return new Promise(() => {}); }
    throw err;
  }));
}

// Pages
const Dashboard = lazyPage(() => import('./pages/Dashboard.jsx'));

// Commercial
const Customers = lazyPage(() => import('./pages/commercial/Customers.jsx'));
const Products = lazyPage(() => import('./pages/commercial/Products.jsx'));
const SalesOrders = lazyPage(() => import('./pages/commercial/SalesOrders.jsx'));
const ForwardBook = lazyPage(() => import('./pages/commercial/ForwardBook.jsx'));
const ForwardBookItemDetail = lazyPage(() => import('./pages/commercial/forwardBook/ForwardBookItemDetail.jsx'));
const ApInvoices = lazyPage(() => import('./pages/commercial/ApInvoices.jsx'));
const SampleRequests = lazyPage(() => import('./pages/sales/SampleRequests.jsx'));
const MarketLandscape = lazyPage(() => import('./pages/sales/MarketLandscape.jsx'));

// QMS — Inventory
const MaterialsProfile = lazyPage(() => import('./pages/qms/MaterialsProfile.jsx'));
const BatchRecordDetail = lazyPage(() => import('./pages/qms/BatchRecordDetail.jsx'));

// QMS — Quality
const Vendors = lazyPage(() => import('./pages/qms/Vendors.jsx'));
const CertificatesHub = lazyPage(() => import('./pages/qms/CertificatesHub.jsx'));
// NAV-SECTION-FOLD-1: QuarantineRecords/MocRecords/TempDeviations now imported
// only by DeviationsHub.jsx (child components), not routed directly here.

// QMS — Admin
const Documents = lazyPage(() => import('./pages/qms/Documents.jsx'));
const Equipment = lazyPage(() => import('./pages/qms/Equipment.jsx'));
const TrainingRecords = lazyPage(() => import('./pages/qms/TrainingRecords.jsx'));
const Onboarding = lazyPage(() => import('./pages/training/Onboarding.jsx'));
const CompetencyPage = lazyPage(() => import('./pages/training/CompetencyPage.jsx'));
const LmsShell = lazyPage(() => import('./pages/training/LmsShell.jsx'));
const LmsCoursePlayer = lazyPage(() => import('./pages/training/LmsCoursePlayer.jsx'));

// HR-ONBOARD-1
const OnboardingWizard = lazyPage(() => import('./pages/hr/OnboardingWizard.jsx'));
const Hr = lazyPage(() => import('./pages/hr/Hr.jsx'));

// Stage 2–4 (Migrations 015–022)
const Rooms = lazyPage(() => import('./pages/qms/Rooms.jsx'));
const ManufacturingRecordTemplates = lazyPage(() => import('./pages/qms/ManufacturingRecordTemplates.jsx'));
const MaterialFamilies = lazyPage(() => import('./pages/qms/MaterialFamilies.jsx'));
const FormulaMaster = lazyPage(() => import('./pages/masters/FormulaMaster.jsx'));
const CompanyMaster = lazyPage(() => import('./pages/masters/CompanyMaster.jsx'));
const CompanyManager = lazyPage(() => import('./pages/companies/CompanyManager.jsx'));
const OpportunityWorkspace = lazyPage(() => import('./pages/companies/OpportunityWorkspace.jsx'));
const PipelineBoard = lazyPage(() => import('./pages/cm/PipelineBoard.jsx'));
const Carriers = lazyPage(() => import('./pages/commercial/Carriers.jsx'));
const Logistics = lazyPage(() => import('./pages/commercial/Logistics.jsx'));
const Form1087 = lazyPage(() => import('./pages/commercial/Form1087.jsx'));
const VAPRecords = lazyPage(() => import('./pages/qms/VAPRecords.jsx'));
const VendorIntakePending = lazyPage(() => import('./pages/qms/VendorIntakePending.jsx'));

// Production

// Warehouse

// Account Settings
const AccountSettings = lazyPage(() => import('./pages/settings/AccountSettings.jsx'));

// Platform Admin
const UserManagement = lazyPage(() => import('./pages/admin/UserManagement.jsx'));
const CustomerApprovedProducts = lazyPage(() => import('./pages/admin/CustomerApprovedProducts.jsx'));
// NAV-SECTION-FOLD-1 §2: CostPoolSettings now imported only by
// approvals/BatchCostingApprovals.jsx (folded in as its second tab).

// Approvals
const BatchCostingApprovals = lazyPage(() => import('./pages/approvals/BatchCostingApprovals.jsx'));

// Finance
const Expenses = lazyPage(() => import('./pages/finance/Expenses.jsx'));
const FinancialIntelligence = lazyPage(() => import('./pages/fpa/FinancialIntelligence.jsx'));

// Inventory stubs

// Quality stubs (individual pages still needed for direct access if not consolidated)

// NAV-CONSOLIDATION-1 — Quality hub pages
const NonConformance = lazyPage(() => import('./pages/quality/NonConformance.jsx'));
const Calibration = lazyPage(() => import('./pages/quality/Calibration.jsx'));
const EnvironmentalMonitoringHub = lazyPage(() => import('./pages/quality/EnvironmentalMonitoringHub.jsx'));
const CorrectiveActions = lazyPage(() => import('./pages/quality/CorrectiveActions.jsx'));
const QualityConfig = lazyPage(() => import('./pages/quality/QualityConfig.jsx'));
// NAV-SECTION-FOLD-1: LabelIssuance now imported only by production/LabelsHub.jsx.

// Sprint 3 — CCP Logs + Customer Complaints
const FoodSafetyHub = lazyPage(() => import('./pages/quality/FoodSafetyHub.jsx'));
// NAV-SECTION-FOLD-1: CustomerComplaints now imported only by NonConformance.jsx.
const NciRecords = lazyPage(() => import('./pages/quality/NciRecords.jsx'));

// Directive H — External Communications
// NAV-SECTION-FOLD-1: ExternalCommunicationLog now imported only by ImprovementCommsHub.jsx.

// Production pages

// Planning pages
// MRP-EXECUTION-SPLIT-1 (2026-09-20): MrpPlanning.jsx's 9-tab hub split into
// two hubs, over the 6-tab hub cap (frontend/CLAUDE.md rule #24). Old file
// removed.
const MrpDemandPlanning = lazyPage(() => import('./pages/planning/MrpDemandPlanning.jsx'));
const MrpExecution = lazyPage(() => import('./pages/planning/MrpExecution.jsx'));

// Stage 6 Pass A — Quality pages
const TrainingPrograms = lazyPage(() => import('./pages/quality/TrainingPrograms.jsx'));
const TrainingEvents = lazyPage(() => import('./pages/quality/TrainingEvents.jsx'));

// Stage 6 Pass B — Quality pages
// NAV-SECTION-FOLD-1: InternalAudits/AnnualProductReviews/ManagementReviews/
// ContinuousImprovement/QualityResponsibility/AllergenDeclarations now
// imported only by their respective hub shells below, not routed directly.
// Quality second-order fold hub shells (2026-09-19):
const DeviationsHub = lazyPage(() => import('./pages/quality/DeviationsHub.jsx'));
const AuditsReviewsHub = lazyPage(() => import('./pages/quality/AuditsReviewsHub.jsx'));
const ImprovementCommsHub = lazyPage(() => import('./pages/quality/ImprovementCommsHub.jsx'));

// Compliance stubs
const AuditTrail = lazyPage(() => import('./pages/compliance/AuditTrail.jsx'));

// VQP-24 — PPE Issuance (standalone /safety/ppe leaf; GMP embedded in SafetyInspections shell)
const PpeIssuanceLogs = lazyPage(() => import('./pages/compliance/PpeIssuanceLogs.jsx'));

// LOTO-LOG-1
const LotoLog = lazyPage(() => import('./pages/safety/LotoLog.jsx'));

// RECALL-MODULE-1
const RecallRecords = lazyPage(() => import('./pages/safety/RecallRecords.jsx'));

// FEEDBACK-TICKET-1
const FeedbackTickets = lazyPage(() => import('./pages/admin/FeedbackTickets.jsx'));

// NAV-CONSOLIDATION-2 — Safety shells
const SafetyPest = lazyPage(() => import('./pages/safety/SafetyPest.jsx'));
const SafetyGlass = lazyPage(() => import('./pages/safety/SafetyGlass.jsx'));
const SafetyEmergency = lazyPage(() => import('./pages/safety/SafetyEmergency.jsx'));
const SafetyChemical = lazyPage(() => import('./pages/safety/SafetyChemical.jsx'));
const SafetyAccess = lazyPage(() => import('./pages/safety/SafetyAccess.jsx'));
const SafetyInspections = lazyPage(() => import('./pages/safety/SafetyInspections.jsx'));
const FacilityCleaningHub = lazyPage(() => import('./pages/safety/FacilityCleaningHub.jsx'));
// NAV-CONSOLIDATION 2026-07-21 — Sediment Tester + Magnet Pull Test merged
// NAV-SECTION-FOLD-1: ForeignMaterialTesting (Sediment Tester + Magnet Pull Test) is now
// folded directly into Calibration.jsx as two more tabs -- ForeignMaterialTesting.jsx itself
// is retired (its two children are imported by Calibration.jsx instead), no route left.
// NAV-SECTION-FOLD-1 §2: BlendRoomOps now imported only by
// production/ProductionBatch.jsx (folded in as its third tab).

const Sustainability = lazyPage(() => import('./pages/quality/sustainability/Sustainability.jsx'));
import EthicsIntakeForm from './pages/quality/sustainability/EthicsIntakeForm.jsx';

// VQP-35 — FDA Registrations (standalone; other inspections embedded in SafetyInspections shell)
const FdaRegistrations = lazyPage(() => import('./pages/compliance/FdaRegistrations.jsx'));

// VQP-14 — Training & Personnel Competency (now rendered by CompetencyPage)

// Admin pages
const DataVault = lazyPage(() => import('./pages/admin/DataVault.jsx'));
const DataVaultFiles = lazyPage(() => import('./pages/admin/DataVaultFiles.jsx'));
const SopLibrary = lazyPage(() => import('./pages/admin/SopLibrary.jsx'));
// NAV-CONSOLIDATION 2026-07-21 — Retention Manager + Disposal Log merged
const RetentionDisposal = lazyPage(() => import('./pages/admin/RetentionDisposal.jsx'));
const DocumentChangeLogs = lazyPage(() => import('./pages/admin/DocumentChangeLogs.jsx'));
const Settings = lazyPage(() => import('./pages/admin/Settings.jsx'));
const Roz = lazyPage(() => import('./pages/admin/Roz.jsx'));
const Leeway = lazyPage(() => import('./pages/admin/Leeway.jsx'));
const Triton = lazyPage(() => import('./pages/admin/Triton.jsx'));
const CostStack = lazyPage(() => import('./pages/admin/CostStack.jsx'));
const Meridian = lazyPage(() => import('./pages/admin/Meridian.jsx'));
// CONSOLE-LINKOUT-1 (2026-09-19): Vigil/Terminus/Specula/Salus/Augur/TokenUsage/CatalogPublish
// imports removed -- their tenant-app routes are gone, ConsoleHome now links out to their real
// admin.littoral.app pages. The 7 page files themselves stay (shared with frontend-admin via
// @shared) -- see docs/directives/proposed/frontend/CONSOLE-LINKOUT-1.md.
// INFRA-ORPHAN-PAGES-1 (2026-09-27, Brian): ConsoleHome import + its own admin/console route
// removed too -- ConsoleHome is an internal ops console, not a tenant-portal page in any form.
// Page file stays on disk (shared with frontend-admin). See docs/directives/futures/infra/INFRA-ORPHAN-PAGES-1.md.
const GlobalSupplierDirectory = lazyPage(() => import('./pages/admin/GlobalSupplierDirectory.jsx'));
const Prospects = lazyPage(() => import('./pages/ing/Prospects.jsx'));
const SalesRequests = lazyPage(() => import('./pages/admin/SalesRequests.jsx'));
const EntityManager = lazyPage(() => import('./pages/admin/EntityManager.jsx'));
const ExternalArImport = lazyPage(() => import('./pages/admin/ExternalArImport.jsx'));
// NAV-SECTION-FOLD-1: AssetLibrary now imported only by production/LabelsHub.jsx.
// NAV-SECTION-FOLD-1 §2: LabelPrinters now imported only by
// production/LabelsHub.jsx (folded in as its third tab).
const LabelsHub = lazyPage(() => import('./pages/production/LabelsHub.jsx'));

// Legal
const Legal = lazyPage(() => import('./pages/legal/Legal.jsx'));

// Littoral™ pages
const LittoralAgent = lazyPage(() => import('./pages/littoral/LittoralAgent.jsx'));
const LittoralStrategy = lazyPage(() => import('./pages/littoral/LittoralStrategy.jsx'));
const Manifest = lazyPage(() => import('./pages/littoral/Manifest.jsx'));
const Latitude = lazyPage(() => import('./pages/littoral/Latitude.jsx'));
const Sonar = lazyPage(() => import('./pages/littoral/Sonar.jsx'));
const Launch = lazyPage(() => import('./pages/littoral/Launch.jsx'));

// Littoral™ Admin pages (UNITE-1 Phase 0)
const LittoralConfig = lazyPage(() => import('./pages/littoral/LittoralConfig.jsx'));
const LittoralTenants = lazyPage(() => import('./pages/littoral/LittoralTenants.jsx'));
const LittoralTenantEditor = lazyPage(() => import('./pages/littoral/LittoralTenantEditor.jsx'));
const LittoralAgents = lazyPage(() => import('./pages/littoral/LittoralAgents.jsx'));

// Reports
const RevenueByCustomer = lazyPage(() => import('./pages/reports/RevenueByCustomer.jsx'));
const BatchYield = lazyPage(() => import('./pages/reports/BatchYield.jsx'));
const NcFrequency = lazyPage(() => import('./pages/reports/NcFrequency.jsx'));
const VendorOnTime = lazyPage(() => import('./pages/reports/VendorOnTime.jsx'));
const InventoryByCategory = lazyPage(() => import('./pages/reports/InventoryByCategory.jsx'));
const ImportTonnage = lazyPage(() => import('./pages/reports/ImportTonnage.jsx'));
const PanjivaVolume = lazyPage(() => import('./pages/reports/PanjivaVolume.jsx'));
const SalesIntelligence = lazyPage(() => import('./pages/rochem/SalesIntelligence.jsx'));
const SalesPerformance = lazyPage(() => import('./pages/reports/SalesPerformance.jsx'));
// AgedInventory / RevenuesDashboard are no longer routed here directly —
// REPORTS-CONSOLIDATION-1 (2026-09-19) folded them into InventoryByCategory.jsx
// and SalesPerformance.jsx as tabs; those hub files import them internally.

import ForgotPassword    from './pages/auth/ForgotPassword.jsx';
import TotpChallenge     from './pages/auth/TotpChallenge.jsx';
import MagicLink         from './pages/auth/MagicLink.jsx';
import ResetPassword     from './pages/auth/ResetPassword.jsx';
const ProductCatalog = lazyPage(() => import('./pages/ing/ProductCatalog.jsx'));
const RfqSourcing = lazyPage(() => import('./pages/ing/RfqSourcing.jsx'));
const RfqDetail = lazyPage(() => import('./pages/ing/RfqDetail.jsx'));
const QuoteTrial = lazyPage(() => import('./pages/ing/QuoteTrial.jsx'));
const IngCostMatrix = lazyPage(() => import('./pages/ing/IngCostMatrix.jsx'));
const IngTariff = lazyPage(() => import('./pages/ing/IngTariff.jsx'));


// NAV-CONSOLIDATION-3 shell imports
const ProductionBatch = lazyPage(() => import('./pages/production/ProductionBatch.jsx'));
const ProductionScheduleHub = lazyPage(() => import('./pages/production/ProductionScheduleHub.jsx'));
const ProductionEquipment = lazyPage(() => import('./pages/production/ProductionEquipment.jsx'));
const ProductionAccountability = lazyPage(() => import('./pages/production/ProductionAccountability.jsx'));
const ProductionCleaning = lazyPage(() => import('./pages/production/ProductionCleaning.jsx'));
const ProductionMaintenance = lazyPage(() => import('./pages/production/ProductionMaintenance.jsx'));
const WarehouseReceiving = lazyPage(() => import('./pages/warehouse/WarehouseReceiving.jsx'));
const WarehouseInventory = lazyPage(() => import('./pages/warehouse/WarehouseInventory.jsx'));
const WarehouseSamples = lazyPage(() => import('./pages/warehouse/WarehouseSamples.jsx'));
const WarehouseMaterials = lazyPage(() => import('./pages/warehouse/WarehouseMaterials.jsx'));
const WarehousePhysical = lazyPage(() => import('./pages/warehouse/WarehousePhysical.jsx'));
const WarehouseAdmin = lazyPage(() => import('./pages/warehouse/WarehouseAdmin.jsx'));
class ErrorBoundary extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(e) { return { error: e }; }
  componentDidCatch(error, info) {
    console.error('EB error:', error);
    console.error('EB type:', typeof error);
    console.error('EB keys:', Object.getOwnPropertyNames(error));
    console.error('EB info:', info.componentStack);
    if (import.meta.env.VITE_SENTRY_DSN) Sentry.captureException(error, { extra: info });
  }
  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (import.meta.env.DEV || this.props.diagnostic) {
      return (
        <div style={{ color: 'var(--color-danger)', padding: '2rem', fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', background: 'var(--color-danger-light)', minHeight: '100vh' }}>
          <strong style={{ fontSize: '1rem', display: 'block', marginBottom: '1rem' }}>Type: {typeof error}</strong>
          <pre>Message: {String(error?.message)}</pre>
          <pre>Name: {String(error?.name)}</pre>
          <pre>Stack: {String(error?.stack)}</pre>
          <pre>Full: {JSON.stringify(error, Object.getOwnPropertyNames(error), 2)}</pre>
        </div>
      );
    }
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-label)' }}>
        <h2 style={{ color: 'var(--color-text-secondary)' }}>Something went wrong</h2>
        <p>Reload the page, or contact support if the problem persists.</p>
        <button
          onClick={() => this.setState({ error: null })}
          style={{ marginTop: '1rem', padding: '0.5rem 1.25rem', background: 'var(--color-accent)', color: 'var(--color-accent-btn-text)', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
        >
          Try again
        </button>
      </div>
    );
  }
}

function AdminRoute({ element }) {
  const { authState } = useCurrentUser();
  if (authState === 'loading') return null;
  return authState === 'admin' ? element : <Navigate to="/dashboard" replace />;
}

// ADMIN-CONSOLE-AUTHZ-GAP-1 (2026-09-20): same loading/redirect shape as AdminRoute, but gates
// on isLittoralAdmin (the LITTORAL_ADMIN_IDS allowlist) rather than any tenant's own admin role.
// For internal fleet-monitoring tooling (Roz/Triton/Manta/Meridian/Mnemon/Leeway) that a
// tenant-side admin should not be able to reach, matching the standard already applied to
// ConsoleHome's 7 external admin.littoral.app cards.
function LittoralAdminRoute({ element }) {
  const { authState, isLittoralAdmin } = useCurrentUser();
  if (authState === 'loading') return null;
  return isLittoralAdmin ? element : <Navigate to="/dashboard" replace />;
}

// ZEBRA-AUDIT-FIX-2 hole 5 (closes QUALITY-CONFIG-ADMINROUTE-ROLE-MISMATCH-1): a role-list
// gate with the same loading/redirect shape as AdminRoute, for pages whose backend already
// permits more than admin (Quality Configuration's Label Templates tab is QA_ADMIN on the
// server; the UI alone was admin-only).
function RoleRoute({ roles, element }) {
  const { authState, role } = useCurrentUser();
  if (authState === 'loading') return null;
  return roles.includes(role) ? element : <Navigate to="/dashboard" replace />;
}

function EmailSignInForm({ name, tagline, primary, cardStyle, endpoint = '/api/roch-auth/login', hideHeader = false }) {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [mfa,      setMfa]      = useState(null); // { mode: 'verify'|'enroll', preAuthToken } — AUTH-TOTP-1
  const trustedDeviceEnabled = !!JSON.parse(sessionStorage.getItem('tenantConfig') || '{}').trustedDeviceEnabled;

  function completeSession({ token, user }) {
    sessionStorage.setItem('magicSession', JSON.stringify({ token, user }));
    window.location.reload();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res  = await fetch(endpoint, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Sign in failed.'); return; }
      if (data.mfaRequired || data.mfaEnrollRequired) {
        setMfa({ mode: data.mfaEnrollRequired ? 'enroll' : 'verify', preAuthToken: data.preAuthToken });
        return;
      }
      completeSession(data);
    } catch {
      setError('Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const inp = {
    width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--color-border-neutral)',
    borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box', marginBottom: '0.75rem',
  };

  if (mfa) {
    return (
      <div style={cardStyle}>
        {!hideHeader && <>
          <div style={{ fontWeight: 800, fontSize: '1.375rem', color: primary, marginBottom: '0.25rem' }}>{name}</div>
          <div style={{ fontSize: '0.7rem', color: 'var(--color-accent)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '1.75rem' }}>{tagline}</div>
        </>}
        <TotpChallenge
          mode={mfa.mode} preAuthToken={mfa.preAuthToken} primary={primary}
          trustedDeviceEnabled={trustedDeviceEnabled}
          onSuccess={completeSession}
          onRestart={() => { setMfa(null); setPassword(''); }}
        />
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      {!hideHeader && <>
        <div style={{ fontWeight: 800, fontSize: '1.375rem', color: primary, marginBottom: '0.25rem' }}>{name}</div>
        <div style={{ fontSize: '0.7rem', color: 'var(--color-accent)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '1.75rem' }}>{tagline}</div>
      </>}
      <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
        <input
          type="email" value={email} placeholder="Email"
          onChange={e => setEmail(e.target.value)}
          required style={inp} autoComplete="username"
        />
        <input
          type="password" value={password} placeholder="Password"
          onChange={e => setPassword(e.target.value)}
          required style={{ ...inp, marginBottom: '1rem' }} autoComplete="current-password"
        />
        {error && <p style={{ color: 'var(--color-danger)', fontSize: '0.8rem', margin: '0 0 0.75rem' }}>{error}</p>}
        <button
          type="submit" disabled={loading}
          style={{ padding: '0.75rem 2rem', background: primary, color: 'var(--color-accent-btn-text)', border: 'none', borderRadius: '0.5rem', fontSize: '0.9375rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', width: '100%', letterSpacing: '0.02em', opacity: loading ? 0.7 : 1 }}
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      {!hideHeader && (
        <a href="/auth/forgot-password" style={{ display: 'block', marginTop: '1rem', fontSize: '0.8rem', color: 'var(--color-text-muted)', textDecoration: 'none' }}>
          Forgot password?
        </a>
      )}
    </div>
  );
}

function SignInPage() {
  const cfg     = JSON.parse(sessionStorage.getItem('tenantConfig') || '{}');
  const name    = cfg.companyName  || import.meta.env.VITE_TENANT_NAME    || 'NutraShore';
  const tagline = cfg.tagLine      || import.meta.env.VITE_TENANT_SUBTITLE || 'QMS Platform';
  const primary = cfg.primaryColor || '#062722';

  const outer = {
    width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    position: 'relative', backgroundImage: 'url(/login-bg.jpg)', backgroundSize: 'cover', backgroundPosition: 'center',
  };
  const cardStyle = {
    textAlign: 'center', padding: '3rem', background: 'var(--color-bg-secondary)', borderRadius: '0.75rem',
    boxShadow: '0 4px 24px rgba(6,39,34,0.12)', maxWidth: '360px', width: '100%', border: '1px solid var(--color-border)',
  };
  const logo = <img src="/ns-logo-blue6.png" alt="Littoral" style={{ position: 'absolute', bottom: '24px', right: '32px', width: '180px', opacity: 0.9 }} />;

  if (!isMsalEnabled) {
    return (
      <div style={outer}>
        <EmailSignInForm name={name} tagline={tagline} primary={primary} cardStyle={cardStyle} />
      </div>
    );
  }

  return (
    <div style={outer}>
      <div style={cardStyle}>
        <div style={{ fontWeight: 800, fontSize: '1.375rem', color: primary, marginBottom: '0.25rem' }}>{name}</div>
        <div style={{ fontSize: '0.7rem', color: 'var(--color-accent)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '1.75rem' }}>{tagline}</div>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '2rem' }}>Sign in with your {name} account to continue.</p>
        <button
          onClick={loginWithRedirect}
          style={{ padding: '0.75rem 2rem', background: primary, color: 'var(--color-accent-btn-text)', border: 'none', borderRadius: '0.5rem', fontSize: '0.9375rem', fontWeight: 700, cursor: 'pointer', width: '100%', letterSpacing: '0.02em' }}
        >
          Sign in with Microsoft
        </button>

      </div>
      {logo}
    </div>
  );
}

// PLATFORM-HARDENING-1 WS1 — router-level auth wrapper (default-deny frontend).
// The auth gates previously rendered AppRoutes on token PRESENCE alone, so a
// stale/invalid magicSession rendered the full app shell for a non-authenticated
// user ("fully rendered while Not signed in" — the app shell, nav, and every page
// mounting API calls that all 401). This validates the session against the backend
// BEFORE the router mounts: pending → nothing; valid → AppRoutes; invalid → the
// session is purged (by the api 401 interceptor) and the sign-in fallback shows.
function ValidatedRoutes({ fallback }) {
  const [state, setState] = useState('checking'); // 'checking' | 'ok' | 'denied'
  useEffect(() => {
    let alive = true;
    api.get('/api/users/me')
      .then(() => { if (alive) setState('ok'); })
      .catch(() => {
        // interceptor already purged the session + may have redirected on a
        // non-auth path; here we just make sure the shell never renders.
        try {
          sessionStorage.removeItem('magicSession');
          sessionStorage.removeItem('__sweep_token');
        } catch { /* ignore */ }
        if (alive) setState('denied');
      });
    return () => { alive = false; };
  }, []);
  if (state === 'checking') return null;      // no shell during validation
  if (state === 'denied')   return fallback;  // sign-in
  return <AppRoutes />;
}

function AppRoutes() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Full-screen player — outside Layout, no sidebar */}
        <Route path="/training/lms/course/:enrollmentId" element={<LmsCoursePlayer />} />
        {/* HR-ONBOARD-1 — full-screen onboarding wizard, outside Layout so a
            Users.Role='onboarding' account sees no sidebar/nav chrome at all.
            Layout.jsx redirects that role here for every other route. */}
        <Route path="/onboarding" element={<OnboardingWizard />} />
        <Route path="/" element={<Layout />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />

              {/* Commercial */}
              <Route path="customers" element={<Customers />} />
              <Route path="products" element={<Products />} />
              <Route path="sales-orders" element={<SalesOrders />} />
              <Route path="forward-book" element={<ForwardBook />} />
              {/* FORWARD-BOOK-ITEM-DETAIL-1 Phase 2 — per-item drill-down, same module gate as the hub. */}
              <Route path="forward-book/:rawMaterialId" element={<ForwardBookItemDetail />} />
              <Route path="sample-requests" element={<SampleRequests />} />
              {/* MARKET-LANDSCAPE-1 — flat route, not nested under /profiles (ruled) */}
              <Route path="market-landscape" element={<MarketLandscape />} />
              {/* MRP-EXECUTION-SPLIT-1 (2026-09-20): Purchase Orders/Requisitions moved to the MRP Execution hub */}
              <Route path="vendor-pos" element={<Navigate to="/planning/mrp-execution?tab=purchase-orders" replace />} />
              <Route path="procurement/requisitions" element={<Navigate to="/planning/mrp-execution?tab=requisitions" replace />} />
              <Route path="goods-receipts" element={<Navigate to="/warehouse/receiving?tab=receipts" replace />} />
              <Route path="ap-invoices" element={<ApInvoices />} />
              <Route path="warehouse/receiving-log"        element={<Navigate to="/warehouse/receiving?tab=log" replace />} />
              <Route path="warehouse/physical-inventory"  element={<Navigate to="/warehouse/physical?tab=physical-inventory" replace />} />
              <Route path="warehouse/zones"               element={<Navigate to="/warehouse/physical?tab=zones" replace />} />
              <Route path="warehouse/manager"             element={<Navigate to="/warehouse/admin?tab=manager" replace />} />
              <Route path="warehouse/reorder-rules"       element={<Navigate to="/warehouse/admin?tab=reorder-rules" replace />} />
              <Route path="warehouse/alerts"              element={<Navigate to="/warehouse/physical?tab=alerts" replace />} />
              <Route path="warehouse/stock-transfers"     element={<Navigate to="/warehouse/inventory?tab=transfers" replace />} />
              <Route path="warehouse/inventory-dashboard" element={<Navigate to="/warehouse/inventory?tab=dashboard" replace />} />
              <Route path="warehouse/coa-intake"         element={<Navigate to="/warehouse/receiving?tab=coa" replace />} />

              {/* QMS Inventory */}
              <Route path="inventory-lots" element={<Navigate to="/warehouse/inventory?tab=lots" replace />} />
              <Route path="raw-materials"     element={<Navigate to="/profiles/materials?tab=raw"      replace />} />
              <Route path="finished-products" element={<Navigate to="/profiles/materials?tab=finished" replace />} />
              <Route path="batch-records" element={<Navigate to="/production/batch?tab=records" replace />} />
              <Route path="batch-records/:id" element={<BatchRecordDetail />} />

              {/* QMS Quality */}
              <Route path="vendors" element={<Vendors />} />
              <Route path="coa-records" element={<CertificatesHub />} />
              <Route path="nc-logs" element={<Navigate to="/quality/non-conformance?tab=log" replace />} />
              {/* NAV-SECTION-FOLD-1: Quarantine + Temp Deviation + MOC folded into Deviations hub */}
              <Route path="quarantine-records" element={<Navigate to="/quality/deviations?tab=quarantine" replace />} />
              <Route path="moc-records" element={<Navigate to="/quality/deviations?tab=moc" replace />} />
              <Route path="scar-records" element={<Navigate to="/quality/corrective-actions?tab=scar" replace />} />
              <Route path="temp-deviations" element={<Navigate to="/quality/deviations?tab=temp-deviations" replace />} />
              <Route path="quality/deviations" element={<DeviationsHub />} />

              {/* QMS Admin */}
              <Route path="documents" element={<Documents />} />
              <Route path="equipment" element={<Equipment />} />
              <Route path="maintenance-requests" element={<Navigate to="/production/maintenance?tab=requests" replace />} />
              <Route path="pm-checklists"        element={<Navigate to="/production/maintenance?tab=pm-checklists" replace />} />
              <Route path="training-records" element={<TrainingRecords />} />
              <Route path="training/onboarding" element={<Onboarding />} />
              <Route path="training/competency" element={<CompetencyPage />} />
              <Route path="training/lms" element={<LmsShell />} />
              <Route path="training/lms-dashboard" element={<Navigate to="/dashboard?tab=lms" replace />} />
              <Route path="vap-records"         element={<VAPRecords />} />
              <Route path="vap-intake-pending"  element={<VendorIntakePending />} />

              {/* Stage 2–4 */}
              <Route path="rooms"                          element={<Rooms />} />
              <Route path="production/rework-orders"          element={<Navigate to="/production/schedule?tab=rework" replace />} />
              <Route path="room-logs"                      element={<Navigate to="/production/batch?tab=room-logs" replace />} />
              <Route path="equipment-operation-logs"       element={<Navigate to="/production/equipment?tab=logs" replace />} />
              <Route path="equipment-configuration-logs"  element={<Navigate to="/production/equipment?tab=config" replace />} />
              <Route path="label-accountability-logs"     element={<Navigate to="/production/accountability?tab=labels" replace />} />
              <Route path="scoop-accountability-logs"     element={<Navigate to="/production/accountability?tab=scoops" replace />} />
              <Route path="cleaning-records"               element={<Navigate to="/production/cleaning?tab=records" replace />} />
              <Route path="material-control-cards"         element={<Navigate to="/warehouse/materials?tab=control-cards" replace />} />
              <Route path="return-authorizations"          element={<Navigate to="/warehouse/materials?tab=return-authorizations" replace />} />
              <Route path="manufacturing-record-templates" element={<ManufacturingRecordTemplates />} />
              <Route path="material-families"              element={<MaterialFamilies />} />
              <Route path="packaging-components"           element={<Navigate to="/profiles/materials?tab=packaging" replace />} />
              <Route path="formula-master"                 element={<FormulaMaster />} />
              <Route path="company-master"                 element={<CompanyMaster />} />
              <Route path="profiles/materials"              element={<MaterialsProfile />} />
              <Route path="profiles/company-master"        element={<CompanyMaster />} />
              <Route path="profiles/companies"             element={<CompanyManager />} />
              <Route path="profiles/companies/:companyId/opportunity/:productId" element={<OpportunityWorkspace />} />
              <Route path="profiles/customers"             element={<CompanyManager roleFilter="customer" />} />
              <Route path="profiles/vendors"               element={<CompanyManager roleFilter="vendor" />} />
              <Route path="profiles/carriers"              element={<CompanyManager roleFilter="carrier" />} />
              <Route path="cm/pipeline-board"              element={<PipelineBoard />} />
              <Route path="retain-samples"                 element={<Navigate to="/warehouse/samples?tab=retain-samples" replace />} />
              <Route path="external-sample-submissions"   element={<Navigate to="/warehouse/samples?tab=external-submissions" replace />} />
              <Route path="lot-pallets"                    element={<Navigate to="/warehouse/inventory?tab=pallets" replace />} />
              <Route path="carriers"                       element={<Carriers />} />
              {/* NAV-CONSOLIDATION-7 — Logistics hub. Old paths redirect to the tabbed shell. */}
              <Route path="logistics"                      element={<Logistics />} />
              <Route path="shipments"                      element={<Navigate to="/logistics?tab=shipments" replace />} />
              <Route path="bills-of-lading"               element={<Navigate to="/logistics?tab=bol" replace />} />
              <Route path="shipping-log"                   element={<Navigate to="/logistics?tab=log" replace />} />
              <Route path="form-1087"                      element={<Form1087 />} />

              {/* Platform Admin */}
              <Route path="users" element={<UserManagement />} />
              {/* HR-ONBOARD-1 — hr_admin (Ricardo) or admin only; matches backend HR_ADMIN group */}
              <Route path="hr" element={<RoleRoute roles={['admin', 'hr_admin']} element={<Hr />} />} />
              <Route path="customer-approved-products" element={<CustomerApprovedProducts />} />
              {/* NAV-SECTION-FOLD-1 §2 (2026-09-19, Brian GO): Cost Pool Settings folded into
                  Batch Costing Approvals as a second tab -- redirect target route below still
                  AdminRoute-wrapped, no separate gate needed here. */}
              <Route path="cost-pool-settings" element={<Navigate to="/approvals/batch-costing?tab=cost-pool" replace />} />

              {/* Approvals */}
              <Route path="approvals/batch-costing" element={<AdminRoute element={<BatchCostingApprovals />} />} />

              {/* Finance */}
              <Route path="expenses" element={<Expenses />} />
              <Route path="finance/overview" element={<Navigate to="/finance/fpa" replace />} />
              <Route path="finance"          element={<Navigate to="/finance/fpa" replace />} />
              <Route path="finance/fpa"                element={<AdminRoute element={<FinancialIntelligence />} />} />
              <Route path="finance/budget-builder"     element={<Navigate to="/finance/fpa?tab=budget" replace />} />
              <Route path="finance/bank-recon"        element={<Navigate to="/finance/fpa?tab=bank-recon" replace />} />
              <Route path="finance/gl/accounts"        element={<Navigate to="/finance/fpa?tab=chart-of-accounts" replace />} />
              <Route path="finance/gl/journal-entries" element={<Navigate to="/finance/fpa?tab=journal-entries" replace />} />

              {/* Inventory (stub pages) */}
              <Route path="inventory/lot-transactions" element={<Navigate to="/warehouse/inventory?tab=transactions" replace />} />
              <Route path="inventory/retain-boxes"     element={<Navigate to="/warehouse/samples?tab=retain-boxes" replace />} />


              {/* Quality (stub pages) */}
              <Route path="quality/test-types"                element={<Navigate to="/quality/config?tab=test-types" replace />} />
              <Route path="quality/capa-records"              element={<Navigate to="/quality/corrective-actions?tab=capa" replace />} />
              <Route path="quality/food-safety"              element={<FoodSafetyHub />} />
              <Route path="quality/food-safety-escalations" element={<Navigate to="/quality/food-safety?tab=escalations" replace />} />
              <Route path="quality/haccp-plans"             element={<Navigate to="/quality/food-safety?tab=haccp" replace />} />
              <Route path="quality/ccp-logs"                element={<Navigate to="/quality/food-safety?tab=ccp" replace />} />
              <Route path="quality/food-defense-assessments" element={<Navigate to="/quality/food-safety?tab=food-defense" replace />} />
              <Route path="quality/fsma-traceability-logs"  element={<Navigate to="/quality/food-safety?tab=fsma" replace />} />
              {/* NAV-SECTION-FOLD-1: Complaints + Returned Product folded into Non-Conformance */}
              <Route path="quality/customer-complaints"     element={<Navigate to="/quality/non-conformance?tab=complaints" replace />} />
              <Route path="quality/nci"                     element={<NciRecords />} />
              <Route path="quality/nc-investigations"       element={<Navigate to="/quality/non-conformance?tab=investigation" replace />} />
              {/* NAV-SECTION-FOLD-1: Continuous Improvement + External Communications folded into Improvement & Comms */}
              <Route path="quality/external-communications" element={<Navigate to="/quality/improvement-comms?tab=external-comms" replace />} />
              {/* SAMPLE-SUBMISSIONS-DEDUP-1 — dead Quality page retired; External Lab Submissions (Form 1069) is the standard */}
              <Route path="quality/sample-submissions"      element={<Navigate to="/warehouse/samples?tab=external-submissions" replace />} />

              {/* NAV-CONSOLIDATION-1 — Quality hub routes */}
              <Route path="quality/non-conformance"    element={<NonConformance />} />
              <Route path="quality/calibration"        element={<Calibration />} />
              <Route path="quality/corrective-actions" element={<CorrectiveActions />} />
              <Route path="quality/config"             element={<RoleRoute roles={['admin', 'qa_manager']} element={<QualityConfig />} />} />
              {/* NAV-SECTION-FOLD-1 (D2 = c, Brian GO): Label Issuance + Asset Library folded into a
                  Labels hub under PRODUCTION. LabelsHub itself is Not wrapped in AdminRoute — its
                  Asset Library tab self-gates admin-only internally (tab-level RBAC), same shape as
                  Quality Config's GDP Audit Log / Food Safety's Escalations, so Label Issuance stays
                  reachable to qa_manager+admin same as before. SymbolLibrary content inside stays
                  reachable regardless of label_printing per the pre-existing note below; only the
                  page's internal Label Template Artwork section self-gates on that module. */}
              <Route path="quality/label-issuance"     element={<Navigate to="/production/labels?tab=issuance" replace />} />
              <Route path="quality/asset-library"      element={<Navigate to="/production/labels?tab=assets" replace />} />
              {/* ORPHAN-CLEANUP-1: WorkOrders.jsx deleted -- unreachable placeholder stub
                  ("under construction"), zero nav/search/help pointer, and "Work Orders" is
                  retired vocabulary per root CLAUDE.md (Ricardo's real workflow is Maintenance
                  Requests / Form 1015). Redirect matches the existing maintenance-requests one. */}
              <Route path="production/work-orders"          element={<Navigate to="/production/maintenance?tab=requests" replace />} />
              <Route path="production/demand-queue"        element={<Navigate to="/production/schedule?tab=demand-queue" replace />} />
              <Route path="production/schedule"            element={<ProductionScheduleHub />} />

              {/* Stage 6 Pass A — Quality */}
              <Route path="quality/training-programs"         element={<TrainingPrograms />} />
              <Route path="quality/training-events"           element={<TrainingEvents />} />
              <Route path="quality/environmental-monitoring"       element={<EnvironmentalMonitoringHub />} />
              <Route path="quality/environmental-monitoring-zones" element={<Navigate to="/quality/environmental-monitoring?tab=zones" replace />} />
              <Route path="quality/atp-swab-logs"               element={<Navigate to="/quality/environmental-monitoring?tab=atp" replace />} />
              <Route path="quality/water-testing-logs"          element={<Navigate to="/quality/environmental-monitoring?tab=water" replace />} />
              <Route path="quality/allergen-profiles"           element={<Navigate to="/quality/config?tab=allergens" replace />} />

              {/* Stage 6 Pass B — Quality */}
              {/* NAV-SECTION-FOLD-1: Internal Audit + APR + Management Review + Quality Responsibility
                  folded into Audits & Reviews */}
              <Route path="quality/internal-audits"           element={<Navigate to="/quality/audits-reviews?tab=internal-audits" replace />} />
              <Route path="quality/annual-product-reviews"    element={<Navigate to="/quality/audits-reviews?tab=annual-product-reviews" replace />} />
              <Route path="quality/management-reviews"        element={<Navigate to="/quality/audits-reviews?tab=management-reviews" replace />} />
              <Route path="quality/audits-reviews"            element={<AuditsReviewsHub />} />
              <Route path="quality/continuous-improvement"    element={<Navigate to="/quality/improvement-comms?tab=ideas" replace />} />
              <Route path="quality/improvement-comms"         element={<ImprovementCommsHub />} />
              <Route path="quality/quality-responsibility"    element={<Navigate to="/quality/audits-reviews?tab=quality-responsibility" replace />} />
              {/* NAV-CONSOLIDATION — folded into Non-Conformance (NAV-SECTION-FOLD-1 re-homed
                  Customer Complaints itself from a standalone hub into an NC tab) */}
              <Route path="quality/returned-product-logs"     element={<Navigate to="/quality/non-conformance?tab=returns" replace />} />
              {/* NAV-SECTION-FOLD-1: Allergen Declarations folded into Food Safety */}
              <Route path="quality/allergen-declarations"     element={<Navigate to="/quality/food-safety?tab=allergen-declarations" replace />} />

              {/* Compliance (stub pages) */}
              <Route path="compliance/audit-trail"            element={<AuditTrail />} />

              {/* Stage 6 Pass A — Compliance */}
              <Route path="compliance/calibration-records"    element={<Navigate to="/quality/calibration?tab=records" replace />} />
              <Route path="compliance/calibration-schedule"   element={<Navigate to="/quality/calibration?tab=schedule" replace />} />

              {/* NAV-CONSOLIDATION-2 — Safety shells */}
              <Route path="safety/pest"        element={<SafetyPest />} />
              <Route path="safety/glass"       element={<SafetyGlass />} />
              <Route path="safety/emergency"   element={<SafetyEmergency />} />
              <Route path="safety/chemical"    element={<SafetyChemical />} />
              <Route path="safety/access"      element={<SafetyAccess />} />
              <Route path="safety/inspections" element={<SafetyInspections />} />
              <Route path="safety/ppe"         element={<PpeIssuanceLogs />} />
              <Route path="safety/loto"             element={<LotoLog />} />
              <Route path="safety/facility-cleaning" element={<Navigate to="/safety/facility-cleaning-hub" replace />} />
              <Route path="safety/facility-cleaning-hub" element={<FacilityCleaningHub />} />
              <Route path="safety/recall-records"  element={<AdminRoute element={<RecallRecords />} />} />
              <Route path="quality/gdp-audit"       element={<Navigate to="/quality/config?tab=gdp-audit" replace />} />
              {/* NAV-CONSOLIDATION — Foreign Material Testing hub; old paths redirect */}
              {/* NAV-SECTION-FOLD-1: Foreign Material Testing folded into Calibration & Testing */}
              <Route path="quality/foreign-material-testing" element={<Navigate to="/quality/calibration?tab=sediment" replace />} />
              <Route path="quality/sediment-tester" element={<Navigate to="/quality/calibration?tab=sediment" replace />} />
              <Route path="quality/magnet-pull-test" element={<Navigate to="/quality/calibration?tab=magnet" replace />} />
              {/* NAV-SECTION-FOLD-1 §2 (2026-09-19, Brian GO): Blend Room Ops folded into the
                  Batch Records hub as a third tab. */}
              <Route path="production/blend-room-ops" element={<Navigate to="/production/batch?tab=blend-room-ops" replace />} />
              {/* NAV-SECTION-FOLD-1 (D2 = c, Brian GO): Labels hub -- Label Issuance + Asset
                  Library, only PRODUCTION change in this fold. Not AdminRoute-wrapped -- the
                  Asset Library tab self-gates admin-only inside LabelsHub so Label Issuance
                  stays reachable to qa_manager+admin. */}
              <Route path="production/labels" element={<LabelsHub />} />
              <Route path="facilities/building-cleaning" element={<Navigate to="/safety/facility-cleaning-hub?tab=building" replace />} />

              {/* NAV-CONSOLIDATION-2 — redirects from old /compliance/* safety paths */}
              <Route path="compliance/pest-control-logs"      element={<Navigate to="/safety/pest?tab=inspections"         replace />} />
              <Route path="compliance/pest-control"           element={<Navigate to="/safety/pest?tab=control"             replace />} />
              <Route path="compliance/glass-breakage"         element={<Navigate to="/safety/glass?tab=log"                replace />} />
              <Route path="compliance/glass-breakage-events"  element={<Navigate to="/safety/glass?tab=events"             replace />} />
              <Route path="compliance/emergency-drills"       element={<Navigate to="/safety/emergency?tab=drills"         replace />} />
              <Route path="compliance/emergency-events"       element={<Navigate to="/safety/emergency?tab=events"         replace />} />
              <Route path="compliance/chemical-inventory"     element={<Navigate to="/safety/chemical?tab=inventory"       replace />} />
              <Route path="compliance/chemical-control"       element={<Navigate to="/safety/chemical?tab=control"         replace />} />
              <Route path="compliance/waste-event-logs"       element={<Navigate to="/safety/chemical?tab=waste-logs"      replace />} />
              <Route path="admin/waste-streams"               element={<Navigate to="/safety/chemical?tab=waste-streams"   replace />} />
              <Route path="compliance/visitor-log"            element={<Navigate to="/safety/access?tab=visitor-log"       replace />} />
              <Route path="compliance/unescorted-access-logs" element={<Navigate to="/safety/access?tab=unescorted"        replace />} />
              <Route path="compliance/security-logs"          element={<Navigate to="/safety/access?tab=security"          replace />} />
              <Route path="compliance/gmp-inspection-logs"    element={<Navigate to="/safety/inspections?tab=gmp"          replace />} />
              <Route path="compliance/regulatory-inspections" element={<Navigate to="/safety/inspections?tab=regulatory"   replace />} />
              <Route path="compliance/ppe-issuance-logs"      element={<Navigate to="/safety/ppe"                          replace />} />


              {/* Planning — MRP-EXECUTION-SPLIT-1 (2026-09-20): two hubs, split from the former 9-tab MrpPlanning.jsx */}
              <Route path="planning/mrp" element={<MrpDemandPlanning />} />
              <Route path="planning/mrp-execution" element={<MrpExecution />} />

              {/* NAV-CONSOLIDATION-3 production shells */}
              <Route path="production/batch"              element={<ProductionBatch />} />
              <Route path="production/batch-records/:id"  element={<BatchRecordDetail />} />
              <Route path="production/equipment"          element={<ProductionEquipment />} />
              <Route path="production/accountability"     element={<ProductionAccountability />} />
              <Route path="production/cleaning"           element={<ProductionCleaning />} />
              <Route path="production/maintenance"        element={<ProductionMaintenance />} />

              {/* NAV-CONSOLIDATION-3 warehouse shells */}
              <Route path="warehouse/receiving"    element={<WarehouseReceiving />} />
              <Route path="warehouse/inventory"    element={<WarehouseInventory />} />
              <Route path="warehouse/samples"      element={<WarehouseSamples />} />
              <Route path="warehouse/materials"    element={<WarehouseMaterials />} />
              <Route path="warehouse/physical"     element={<WarehousePhysical />} />
              <Route path="warehouse/admin"        element={<AdminRoute element={<WarehouseAdmin />} />} />
              {/* VQP-32 — Cleaning Validation */}
              <Route path="quality/cleaning-validation" element={<Navigate to="/quality/config?tab=cleaning-validation" replace />} />

              {/* VQP-36 — Equipment Qualifications */}
              <Route path="quality/equipment-qualifications" element={<Navigate to="/quality/config?tab=equipment-qualifications" replace />} />

              {/* ECOVADIS-6 — Sustainability */}
              <Route path="quality/sustainability"                      element={<Sustainability />} />
              <Route path="quality/sustainability/assessments"       element={<Navigate to="../sustainability?tab=assessments"    replace />} />
              <Route path="quality/sustainability/cap-items"         element={<Navigate to="../sustainability?tab=cap-items"      replace />} />
              <Route path="quality/sustainability/emissions"         element={<Navigate to="../sustainability?tab=emissions"      replace />} />
              <Route path="quality/sustainability/evidence"          element={<Navigate to="../sustainability?tab=evidence"       replace />} />
              <Route path="quality/sustainability/evidence-library"  element={<Navigate to="../sustainability?tab=evidence"       replace />} />
              <Route path="quality/sustainability/certifications"    element={<Navigate to="../sustainability?tab=certifications" replace />} />
              <Route path="quality/sustainability/ethics-reports"    element={<Navigate to="../sustainability?tab=ethics"         replace />} />

              {/* VQP-35 — FDA Registrations (standalone — Ricardo confirm: move to /safety/inspections?tab=fda?) */}
              <Route path="compliance/fda-registrations" element={<FdaRegistrations />} />

              {/* VQP-14 — moved to /training/competency?tab=skill-records */}
              <Route path="compliance/competency-records" element={<Navigate to="/training/competency?tab=skill-records" replace />} />
              <Route path="compliance/background-check-log" element={<Navigate to="/training/onboarding?tab=background-check" replace />} />

              {/* Directive V — Disposal Log → NAV-CONSOLIDATION: merged into Retention & Disposal */}
              <Route path="compliance/disposal-log"           element={<Navigate to="/admin/retention?tab=disposal" replace />} />

              {/* Legal */}
              <Route path="legal" element={<Legal />} />

              {/* Reports */}
              <Route path="reports/revenue-by-customer"  element={<RevenueByCustomer />} />
              <Route path="reports/batch-yield"          element={<BatchYield />} />
              <Route path="reports/nc-frequency"         element={<NcFrequency />} />
              <Route path="reports/vendor-on-time"       element={<VendorOnTime />} />
              <Route path="reports/inventory-by-category" element={<InventoryByCategory />} />
              {/* REPORTS-CONSOLIDATION-1 (2026-09-19): Aged Inventory folded into the
                  Inventory by Category hub as its "Aged" tab. */}
              <Route path="reports/aged-inventory"       element={<Navigate to="/reports/inventory-by-category?tab=aged" replace />} />
              <Route path="reports/import-tonnage"       element={<ImportTonnage />} />
              <Route path="reports/panjiva-volume"       element={<PanjivaVolume />} />
              <Route path="reports/sales-intelligence"   element={<AdminRoute element={<CapGate cap="RochHistoricalSales" element={<SalesIntelligence />} />} />} />
              {/* REPORTS-CONSOLIDATION-1 (2026-09-19): Revenue Dashboard folded into the
                  Sales Performance hub as its "Revenue Dashboard" tab — the target route
                  below already carries the same AdminRoute + CapGate. */}
              <Route path="reports/revenue-dashboard"   element={<Navigate to="/reports/sales-performance?tab=revenue" replace />} />
              <Route path="reports/sales-performance"   element={<AdminRoute element={<CapGate cap="RochHistoricalSales" element={<SalesPerformance />} />} />} />

              {/* Admin pages */}
              <Route path="admin/cleaning-checklist-templates" element={<Navigate to="/production/cleaning?tab=templates" replace />} />
              {/* admin/waste-streams redirected to /safety/chemical?tab=waste-streams — NAV-CONSOLIDATION-2 */}
              <Route path="admin/sop-library"          element={<SopLibrary />} />
              <Route path="admin/retention"            element={<AdminRoute element={<RetentionDisposal />} />} />
              <Route path="admin/document-change-logs" element={<DocumentChangeLogs />} />
              <Route path="admin/data-vault"           element={<CapGate cap="RochHistoricalSales" element={<DataVault />} />} />
              <Route path="admin/data-vault-files"   element={<DataVaultFiles />} />
              <Route path="settings"                 element={<AccountSettings />} />
              <Route path="admin/settings"           element={<Settings />} />
              <Route path="admin/roz"                element={<LittoralAdminRoute element={<Roz />} />} />
              <Route path="admin/leeway"             element={<LittoralAdminRoute element={<Leeway />} />} />
              <Route path="admin/triton"             element={<LittoralAdminRoute element={<Triton />} />} />
              <Route path="admin/manta"              element={<LittoralAdminRoute element={<CostStack />} />} />
              <Route path="admin/cost-stack"         element={<Navigate to="/admin/manta" replace />} />
              <Route path="admin/meridian"           element={<LittoralAdminRoute element={<Meridian />} />} />
              <Route path="admin/supplier-directory" element={<AdminRoute element={<GlobalSupplierDirectory />} />} />
              <Route path="cm/producers"             element={<Navigate to="/profiles/companies?view=directory" replace />} />
              <Route path="admin/producers"          element={<Navigate to="/profiles/companies?view=directory" replace />} />
              <Route path="admin/sales-requests"        element={<AdminRoute element={<SalesRequests />} />} />
              {/* NAV-PLACEMENT-1 — EDI Monitor is now the "EDI Monitor" tab on /sales-orders (Sales, not Admin) */}
              <Route path="admin/edi-monitor"           element={<Navigate to="/sales-orders?tab=edi-monitor" replace />} />
              <Route path="admin/entities"              element={<AdminRoute element={<EntityManager />} />} />
              <Route path="admin/external-ar"           element={<AdminRoute element={<ExternalArImport />} />} />
              {/* NAV-SECTION-FOLD-1 §2 (2026-09-19, Brian GO): Label Printers folded into the
                  Labels hub as a third tab. */}
              <Route path="admin/label-printers"        element={<Navigate to="/production/labels?tab=printers" replace />} />
              <Route path="admin/feedback-tickets"       element={<AdminRoute element={<FeedbackTickets />} />} />
              <Route path="admin/symbol-library"        element={<Navigate to="/production/labels?tab=assets" replace />} />

              {/* Littoral™ pages */}
              <Route path="littoral/strategy"  element={<LittoralStrategy />} />
              <Route path="littoral/sonar"     element={<Sonar />} />
              <Route path="littoral/manifest"  element={<Manifest />} />
              <Route path="littoral/latitude"  element={<Latitude />} />
              <Route path="littoral/launch"    element={<Launch />} />
              <Route path="littoral/agent"     element={<LittoralAdminRoute element={<LittoralAgent />} />} />

              {/* Littoral™ Admin (UNITE-1 Phase 0) */}
              <Route path="littoral/config"             element={<AdminRoute element={<LittoralConfig />} />} />
              <Route path="littoral/platform-config"    element={<AdminRoute element={<LittoralConfig />} />} />
              <Route path="littoral/tenants"            element={<AdminRoute element={<LittoralTenants />} />} />
              <Route path="littoral/tenants/:slug"      element={<AdminRoute element={<LittoralTenantEditor />} />} />
              <Route path="littoral/tenant-registry"    element={<AdminRoute element={<LittoralTenants />} />} />
              <Route path="littoral/tenant-registry/:slug" element={<AdminRoute element={<LittoralTenantEditor />} />} />
              <Route path="littoral/agents"             element={<AdminRoute element={<LittoralAgents />} />} />
              <Route path="littoral/agent-directory"    element={<AdminRoute element={<LittoralAgents />} />} />

              {/* INGREDIENT MGMT — product_management module */}
              <Route path="ing/catalog"            element={<ProductCatalog />} />
              <Route path="ing/sourcing"           element={<RfqSourcing />} />
              <Route path="ing/prospects"          element={<Prospects />} />
              <Route path="ing/sourcing/:id"        element={<RfqDetail />} />
              <Route path="ing/quotes"             element={<QuoteTrial />} />
              <Route path="ing/cost-matrix"        element={<IngCostMatrix />} />
              <Route path="ing/tariff"             element={<IngTariff />} />
              {/* DEAD-ON-LOAD-1 (2026-09-27): retired per the standing ING-SETTINGS-ORPHAN-1
                  ruling (2026-07-21, "delete, don't patch") -- never had a live backend, at
                  any point in its history. Everything it attempted now lives on /settings. */}
              <Route path="ing/settings"           element={<Navigate to="/settings" replace />} />
              <Route path="ing/import-history"     element={<Navigate to="/ing/cost-matrix?tab=cost-intake" replace />} />
              {/* Legacy ING routes — redirect to hub tabs */}
              <Route path="ing/fob-calculator"     element={<Navigate to="/ing/quotes?tab=fob" replace />} />
              <Route path="ing/sell-sheet"         element={<Navigate to="/ing/quotes?tab=sell-sheet" replace />} />
              <Route path="ing/quote-builder"      element={<Navigate to="/ing/quotes" replace />} />
              <Route path="ing/quote-history"      element={<Navigate to="/ing/quotes" replace />} />
              <Route path="ing/cost-stack"         element={<Navigate to="/ing/cost-matrix?tab=cost-stack" replace />} />
              <Route path="ing/tariff-schedule"    element={<Navigate to="/ing/tariff?tab=tariff-schedule" replace />} />
              <Route path="ing/freight-rates"      element={<Navigate to="/ing/cost-matrix?tab=freight-rates" replace />} />
              <Route path="ing/customer-directory" element={<Navigate to="/ing/tariff?tab=company-directory" replace />} />

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
      </Suspense>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

// Auth gate for email-auth tenants (Rochem, AMPAK) — no MsalProvider needed.
function EmailAuthGate() {
  // Public path intercept — before any auth check
  const _path = window.location.pathname;
  const _cfg = JSON.parse(sessionStorage.getItem('tenantConfig') || '{}');
  if (_cfg.TenantSlug === 'rochem' && _path === '/ethics') {
    return <EthicsIntakeForm />;
  }

  const sweepToken = sessionStorage.getItem('__sweep_token');
  if (sweepToken) {
    sessionStorage.setItem('magicSession', JSON.stringify({ token: sweepToken }));
    return <AppRoutes />;
  }
  const hasSession = (() => {
    try { return !!JSON.parse(sessionStorage.getItem('magicSession'))?.token; } catch { return false; }
  })();
  // Auth pages take priority even with a (possibly stale) session token present.
  const path = window.location.pathname;
  if (path.startsWith('/auth/forgot-password')) return <ForgotPassword />;
  if (path.startsWith('/auth/reset-password'))  return <ResetPassword />;
  if (path.startsWith('/auth/verify'))           return <MagicLink />;
  // PLATFORM-HARDENING-1: validate the token before rendering the app shell.
  if (hasSession) return <ValidatedRoutes fallback={<SignInPage />} />;
  return <SignInPage />;
}

// Guards against rendering SignInPage while MSAL is mid-redirect,
// which would trigger a second loginRedirect and cause a loop.
function AuthGate() {
  const { inProgress } = useMsal();
  const processing = inProgress !== InteractionStatus.None;

  // Public path intercept — after hooks, before auth gate logic
  const _path2 = window.location.pathname;
  const _cfg = JSON.parse(sessionStorage.getItem('tenantConfig') || '{}');
  if (_cfg.TenantSlug === 'rochem' && _path2 === '/ethics') {
    return <EthicsIntakeForm />;
  }

  // Tidal Sweep smoke-test bypass — real security is enforced by backend token verification
  const sweepToken = sessionStorage.getItem('__sweep_token');
  if (sweepToken) {
    sessionStorage.setItem('magicSession', JSON.stringify({ token: sweepToken }));
    return <AppRoutes />;
  }

  // Email-auth (magic link) session — bypass MSAL entirely
  const hasMagicSession = (() => {
    try { return !!JSON.parse(sessionStorage.getItem('magicSession'))?.token; } catch { return false; }
  })();
  // Auth pages — no MSAL required
  const path = window.location.pathname;
  if (path.startsWith('/auth/forgot-password')) return <ForgotPassword />;
  if (path.startsWith('/auth/reset-password'))  return <ResetPassword />;
  if (path.startsWith('/auth/verify'))           return <MagicLink />;

  // PLATFORM-HARDENING-1: validate a magic-link session before the app shell.
  if (hasMagicSession) return <ValidatedRoutes fallback={<SignInPage />} />;

  return (
    <>
      {/* MSAL tenants: AuthenticatedTemplate already keys off real MSAL state,
          so the shell only renders for a genuinely authenticated user. */}
      <AuthenticatedTemplate>
        <AppRoutes />
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        {!processing && <SignInPage />}
      </UnauthenticatedTemplate>
    </>
  );
}

function PublicRouteInterceptor({ children }) {
  const path = window.location.pathname;
  if (path === '/ethics') return <EthicsIntakeForm />;
  return children;
}

export default function App() {
  /* Sync document.title to the tenant's companyName once on mount.
   * main.jsx already sets it pre-React; this is a safety net for cases where
   * the sessionStorage value wasn't available before the IIFE completed. */
  useEffect(() => {
    try {
      const cfg = JSON.parse(sessionStorage.getItem('tenantConfig') || '{}');
      if (cfg.companyName) document.title = cfg.companyName;
    } catch { /* ignore */ }
  }, []);

  if (!isMsalEnabled) return <PublicRouteInterceptor><EmailAuthGate /></PublicRouteInterceptor>;

  return (
    <PublicRouteInterceptor>
      <MsalProvider instance={getMsalInstance()}>
        <AuthGate />
      </MsalProvider>
    </PublicRouteInterceptor>
  );
}
